/* -------------------------------------------------------------------------
 *  Product data for the three Leadership System pages.
 *  Unified architecture per the July Leadership System Pages brief:
 *    1. Hero  2. Leadership Reality  3. Hidden Execution Gap™
 *    4. A Better Leadership System   4b. Composite Scenario
 *    5. How It Works (each step has a supporting sentence)
 *    6. What Changes   7. Impact at Every Level (+ District Lens callout)
 *    8. Why AlignED Thinks Differently (shared, defined in ProductPage.jsx)
 *    9. Start the Leadership Conversation (domain-specific per page)
 *
 *  Each entry is a data-only object. Rendering lives in ProductPage.jsx.
 * ------------------------------------------------------------------------- */

export const PRODUCTS = {
  "attendance-intervention-system": {
    slug: "attendance-intervention-system",
    path: "/solutions/attendance-intervention-system",
    eyebrow: "Attendance Intervention System\u2122",
    seoTitle: "Attendance Intervention System\u2122 | AlignED",
    seoDescription:
      "Turn attendance intervention into consistent execution. AlignED helps schools and districts strengthen attendance follow-through, visibility, ownership, and learning.",
    hero: {
      headline: "Turn attendance intervention into consistent execution.",
      supportingCopy:
        "AlignED helps schools and districts strengthen attendance intervention by improving visibility, follow-through, ownership, and learning across the full support process.",
    },
    reality: {
      lead: "Attendance teams usually know which students need support.",
      closer: "The challenge is making sure support happens consistently after concern is identified.",
      bullets: [
        "Students are identified, but follow-up varies.",
        "Intervention steps depend on the school or staff member.",
        "Leaders cannot always see where support is breaking down.",
        "Families may receive inconsistent communication.",
        "Successful practices stay isolated.",
      ],
    },
    executionGap: {
      name: "Attendance Execution Gap\u2122",
      copy: "The attendance challenge is rarely awareness alone. It is the gap between identifying a student and ensuring dependable follow-through.",
    },
    system: {
      name: "Attendance Intervention System\u2122",
      description:
        "helps leaders close the Attendance Execution Gap\u2122 by making intervention follow-through more visible, consistent, and learnable.",
    },
    scenario:
      "A student begins missing school every Monday. The absence is noticed, but the second follow-up depends on who owns the next step and whether leadership can see that it happened. The Attendance Intervention System\u2122 helps make that follow-through visible before the pattern becomes harder to reverse.",
    howItWorks: [
      {
        step: "Identify",
        body: "Surface the students and attendance patterns that deserve leadership attention.",
      },
      {
        step: "Prioritize",
        body: "Focus support where follow-through can create the greatest difference.",
      },
      {
        step: "Follow Through",
        body: "Make ownership, next steps, and intervention progress more visible.",
      },
      {
        step: "Review",
        body: "Help leaders understand where support is working and where execution is drifting.",
      },
      {
        step: "Learn",
        body: "Capture what improves attendance follow-through so successful practices can be repeated.",
      },
    ],
    whatChanges: {
      before: [
        "Attendance concerns are visible, but follow-up varies.",
        "Leaders rely on reminders.",
        "Intervention ownership is unclear.",
        "Practices differ across schools.",
      ],
      after: [
        "Follow-through becomes visible.",
        "Ownership becomes clearer.",
        "Leadership conversations become more focused.",
        "Successful practices can be repeated.",
      ],
    },
    impact: {
      district:
        "Greater consistency across schools and clearer visibility into attendance intervention patterns.",
      school:
        "Stronger routines, clearer ownership, and better follow-through with students and families.",
      frontline:
        "Less ambiguity, clearer next steps, and more confidence in daily attendance support.",
    },
    districtLens:
      "Attendance improvement depends on more than identifying students. District leaders need to see whether intervention follow-through is happening consistently across schools.",
    finalCta: {
      headline: "Strengthen attendance follow-through.",
      copy: "If your school or district can identify attendance concerns but follow-through still varies, AlignED can help make the support process more visible, consistent, and learnable.",
    },
  },

  "behavior-consistency-system": {
    slug: "behavior-consistency-system",
    path: "/solutions/behavior-consistency-system",
    eyebrow: "Behavior Consistency System\u2122",
    seoTitle: "Behavior Consistency System\u2122 | AlignED",
    seoDescription:
      "Help behavior expectations become consistent daily practice. AlignED helps schools and districts strengthen behavior systems through visibility, alignment, and follow-through.",
    hero: {
      headline: "Help behavior expectations become consistent daily practice.",
      supportingCopy:
        "AlignED helps schools and districts strengthen behavior systems by improving consistency, visibility, follow-through, and shared learning across teams.",
    },
    reality: {
      lead: "Most schools have behavior expectations.",
      closer:
        "The challenge is ensuring those expectations are reinforced consistently across classrooms, staff, and situations.",
      bullets: [
        "Expectations are communicated, but applied unevenly.",
        "Staff responses vary.",
        "Leaders see incidents, but not always the patterns behind them.",
        "Strong practices exist but do not spread consistently.",
        "Behavior conversations become reactive.",
      ],
    },
    executionGap: {
      name: "Behavior Execution Gap\u2122",
      copy: "The behavior challenge is rarely the absence of expectations. It is the gap between stated expectations and consistent adult practice.",
    },
    system: {
      name: "Behavior Consistency System\u2122",
      description:
        "helps leaders close the Behavior Execution Gap\u2122 by making expectations, responses, patterns, and follow-through more visible and consistent.",
    },
    scenario:
      "Two classrooms respond differently to the same recurring behavior. Both adults care, but the student experience changes depending on where the behavior occurs. The Behavior Consistency System\u2122 helps leaders see response patterns and strengthen consistency across the school.",
    howItWorks: [
      {
        step: "Observe",
        body: "Surface behavior patterns, response variation, and consistency concerns.",
      },
      {
        step: "Recognize Patterns",
        body: "Help leaders understand whether incidents reflect isolated events or recurring execution patterns.",
      },
      {
        step: "Align Responses",
        body: "Support clearer expectations and more consistent adult practice.",
      },
      {
        step: "Follow Through",
        body: "Make ownership, response, and support steps easier to see.",
      },
      {
        step: "Reflect",
        body: "Capture what is working so behavior systems improve over time.",
      },
    ],
    whatChanges: {
      before: [
        "Behavior expectations are stated, but consistency varies.",
        "Leaders react to incidents.",
        "Staff alignment depends on reminders.",
        "Patterns are difficult to see early.",
      ],
      after: [
        "Patterns become more visible.",
        "Leadership conversations become clearer.",
        "Staff alignment improves.",
        "Consistency strengthens over time.",
      ],
    },
    impact: {
      district:
        "Clearer visibility into behavior consistency across schools and stronger system-wide learning.",
      school:
        "Better staff alignment, clearer expectations, and more focused leadership conversations.",
      frontline:
        "More consistent guidance, clearer responses, and reduced uncertainty in daily practice.",
    },
    districtLens:
      "Behavior consistency depends on more than shared expectations. District leaders need visibility into how expectations are reinforced across schools, teams, and settings.",
    finalCta: {
      headline: "Strengthen behavior consistency.",
      copy: "If expectations are clear but adult practice still varies, AlignED can help leadership teams see patterns, align responses, and strengthen consistency over time.",
    },
  },

  "enrollment-follow-through-system": {
    slug: "enrollment-follow-through-system",
    path: "/solutions/enrollment-follow-through-system",
    eyebrow: "Enrollment Follow-Through System\u2122",
    seoTitle: "Enrollment Follow-Through System\u2122 | AlignED",
    seoDescription:
      "Make enrollment follow-through visible, dependable, and consistent. AlignED helps schools and districts strengthen family communication, ownership, and learning.",
    hero: {
      headline: "Make enrollment follow-through visible, dependable, and consistent.",
      supportingCopy:
        "AlignED helps schools and districts strengthen enrollment follow-through by improving visibility, ownership, family communication, and learning across the enrollment process.",
    },
    reality: {
      lead: "Enrollment does not usually break because families are uninterested.",
      closer: "It often breaks because follow-up becomes inconsistent.",
      bullets: [
        "Families inquire but do not receive consistent follow-up.",
        "Ownership is unclear after the first contact.",
        "Communication varies by staff member or school.",
        "Leaders cannot always see where families lose momentum.",
        "Enrollment learning is not captured year to year.",
      ],
    },
    executionGap: {
      name: "Enrollment Execution Gap\u2122",
      copy: "The enrollment challenge is rarely interest alone. It is the gap between family interest and dependable follow-through.",
    },
    system: {
      name: "Enrollment Follow-Through System\u2122",
      description:
        "helps leaders close the Enrollment Execution Gap\u2122 by making family communication, ownership, and next steps more visible and consistent.",
    },
    scenario:
      "A family submits an enrollment inquiry and receives an initial response. After that, momentum depends on whether ownership, timing, and follow-up are clear. The Enrollment Follow-Through System\u2122 helps schools make the family journey more visible and dependable.",
    howItWorks: [
      {
        step: "Capture Interest",
        body: "Make family interest and enrollment inquiries visible from the beginning.",
      },
      {
        step: "Assign Ownership",
        body: "Clarify who is responsible for follow-up and next steps.",
      },
      {
        step: "Follow Up",
        body: "Support consistent communication so families do not lose momentum.",
      },
      {
        step: "Review Progress",
        body: "Help leaders see where enrollment conversations are moving forward or stalling.",
      },
      {
        step: "Learn",
        body: "Capture what improves family follow-through so future enrollment cycles become stronger.",
      },
    ],
    whatChanges: {
      before: [
        "Family interest is captured, but follow-up varies.",
        "Leaders lack visibility into stalled inquiries.",
        "Communication depends on individual habits.",
        "Enrollment learning disappears after the cycle ends.",
      ],
      after: [
        "Follow-up becomes more dependable.",
        "Ownership becomes visible.",
        "Family communication improves.",
        "Schools learn from each enrollment cycle.",
      ],
    },
    impact: {
      district:
        "Better visibility into enrollment pipelines, follow-through consistency, and family experience across schools.",
      school: "Clearer ownership, stronger communication routines, and better enrollment momentum.",
      frontline: "Simpler follow-up, clearer next steps, and reduced friction in supporting families.",
    },
    districtLens:
      "Enrollment strength depends on more than generating interest. District leaders need to see whether family follow-through is consistent across schools and enrollment cycles.",
    finalCta: {
      headline: "Strengthen enrollment follow-through.",
      copy: "If family interest is present but follow-up depends too much on individual habits, AlignED can help make enrollment communication more visible, dependable, and consistent.",
    },
  },
};
